import { paymentSessionService } from './paymentSessionService';
import { currencyService } from './currencyService';

export type ClientStatus = 'pending' | 'active' | 'completed' | 'canceled';
export type ClientSource = 'manual' | 'payment';

export interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  companyNumber?: string;
  purchaseDate: string;
  serviceId: string;
  serviceName: string;
  status: ClientStatus;
  notes?: string;
  totalAmount: number;
  currencyCode: string;
  source: ClientSource;
  lastContactAt?: string;
}

interface ClientRow {
  client_key: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  address: string | null;
  company_number: string | null;
  purchase_date: string | null;
  service_key: string | null;
  service_name: string | null;
  status: ClientStatus | null;
  notes: string | null;
  total_amount: number | string | null;
  currency_code: string | null;
  source: ClientSource | null;
  last_contact_at: string | null;
}

let clientsCache: Client[] | null = null;

const generateClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `client_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeDate = (value?: string | null): string => {
  if (!value) {
    return new Date().toISOString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
};

const sortClients = (clients: Client[]): Client[] =>
  [...clients].sort(
    (left, right) =>
      new Date(right.purchaseDate).getTime() - new Date(left.purchaseDate).getTime()
  );

const parseAdminResponse = async <T,>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || 'Impossible de gérer les clients.');
  }
  return payload as T;
};

const mapClientRow = (row: ClientRow): Client => ({
  id: row.client_key,
  name: row.name,
  email: row.email,
  phone: row.phone ?? '',
  company: row.company ?? '',
  address: row.address ?? '',
  companyNumber: row.company_number ?? '',
  purchaseDate: normalizeDate(row.purchase_date),
  serviceId: row.service_key ?? '',
  serviceName: row.service_name ?? '',
  status: row.status ?? 'pending',
  notes: row.notes ?? '',
  totalAmount: Number(row.total_amount ?? 0),
  currencyCode: row.currency_code ?? 'EUR',
  source: row.source ?? 'manual',
  lastContactAt: row.last_contact_at ? normalizeDate(row.last_contact_at) : '',
});

const sanitizeClient = (client: Client): Client => ({
  id: client.id || generateClientId(),
  name: client.name.trim(),
  email: client.email.trim().toLowerCase(),
  phone: client.phone?.trim() || '',
  company: client.company?.trim() || '',
  address: client.address?.trim() || '',
  companyNumber: client.companyNumber?.trim() || '',
  purchaseDate: normalizeDate(client.purchaseDate),
  serviceId: client.serviceId?.trim() || '',
  serviceName: client.serviceName?.trim() || '',
  status: client.status || 'pending',
  notes: client.notes?.trim() || '',
  totalAmount: Number(client.totalAmount || 0),
  currencyCode: client.currencyCode?.trim().toUpperCase() || 'EUR',
  source: client.source || 'manual',
  lastContactAt: client.lastContactAt ? normalizeDate(client.lastContactAt) : '',
});

export const createEmptyClient = (): Client => ({
  id: generateClientId(),
  name: '',
  email: '',
  phone: '',
  company: '',
  address: '',
  companyNumber: '',
  purchaseDate: new Date().toISOString(),
  serviceId: '',
  serviceName: '',
  status: 'pending',
  notes: '',
  totalAmount: 0,
  currencyCode: currencyService.getDisplayCurrencyCode(),
  source: 'manual',
  lastContactAt: '',
});

export async function getAllClients(forceRefresh = false): Promise<Client[]> {
  if (!forceRefresh && clientsCache !== null) {
    return clientsCache;
  }

  const response = await fetch('/api/admin/clients', {
    credentials: 'include',
    cache: 'no-store',
  });
  const { clients } = await parseAdminResponse<{ clients?: ClientRow[] }>(response);

  clientsCache = sortClients((clients ?? []).map(mapClientRow));
  return clientsCache;
}

export async function saveClient(client: Client): Promise<Client> {
  const sanitized = sanitizeClient(client);

  if (!sanitized.name || !sanitized.email) {
    throw new Error('Le nom et l’email sont obligatoires.');
  }

  const response = await fetch('/api/admin/clients', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: sanitized }),
  });
  await parseAdminResponse<{ ok: true }>(response);

  clientsCache = null;
  return sanitized;
}

export async function removeClient(clientId: string): Promise<void> {
  const response = await fetch('/api/admin/clients', {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ clientId }),
  });
  await parseAdminResponse<{ ok: true }>(response);

  clientsCache = null;
}

export async function captureClientFromPayment(token: string): Promise<Client | null> {
  const session = paymentSessionService.getSession(token);

  if (!session?.customer || !session.customer.email) {
    return null;
  }

  const name = [session.customer.firstName, session.customer.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const client = sanitizeClient({
    id: token,
    name: name || session.customer.email,
    email: session.customer.email,
    phone: session.customer.phone || '',
    company: session.company || '',
    address: '',
    companyNumber: '',
    purchaseDate: session.createdAt || new Date().toISOString(),
    serviceId: session.serviceId || '',
    serviceName: session.serviceName || '',
    status: 'active',
    notes: `Paiement confirme via Mollie (${session.paymentType === 'klarna' ? 'Klarna' : 'Carte bancaire'}).`,
    totalAmount: session.amount || 0,
    currencyCode: session.currencyCode || 'EUR',
    source: 'payment',
    lastContactAt: new Date().toISOString(),
  });

  await saveClient(client);
  return client;
}

export function clearClientCache(): void {
  clientsCache = null;
}

export const clientService = {
  getClients: getAllClients,
  saveClient,
  removeClient,
  createEmptyClient,
  captureClientFromPayment,
  clearCache: clearClientCache,
};
